/**
 * Home Screen - canonical game catalog entry point.
 */

import { useState } from "react";
import {
  buildGameReplayPath,
  buildReplayLobbyPath,
  navigateTo,
} from "../router";
import {
  getUserLabel,
  useAuth,
  useAuthActions,
} from "../shared/store/auth-store";
import { Alert, Button } from "../ui";
import { GameCatalogList, useGameCatalog } from "./GameCatalogList";
import { useGameDocsIndex } from "./game-docs";
import {
  emptyGameListFilters,
  type GameListFilters,
  type GameSortMode,
  gameSortOptions,
  getVisibleGameTypes,
} from "./game-list-controls";

/**
 * Backend-free replay/simulation entries surfaced from the home screen. These
 * load a saved game event file and run it (auto/manual) via replay mode — no
 * live multiplayer backend required.
 */
const REPLAY_ENTRIES = [
  {
    gameType: "carnival-of-sins",
    scenario: "heuristic-4p",
    title: "Carnival of Sins · 죄악의 카니발",
    description: "휴리스틱 AI 시뮬레이션 · 4인 7라운드 세트-컬렉션 다이스",
    icon: "🎭",
  },
  {
    gameType: "run-horses",
    scenario: "heuristic-2p",
    title: "말달리자",
    description: "휴리스틱 AI 자가대국 시뮬레이션 · 11×11 오아시스 선착",
    icon: "🐎",
  },
  {
    gameType: "memory-feast",
    scenario: "heuristic-2p",
    title: "기억의 만찬",
    description: "휴리스틱 AI 시뮬레이션 · 같은 수 그릇쌍 찾기 (숨김정보)",
    icon: "🍲",
  },
  {
    gameType: "oblivion-mine",
    scenario: "heuristic-2p",
    title: "망각의 지뢰",
    description:
      "휴리스틱 AI 시뮬레이션 · 지뢰 매설·추리·보물 레이스 (비대칭 숨김)",
    icon: "💣",
  },
  {
    gameType: "show-me-the-coin",
    scenario: "heuristic-2p",
    title: "쇼 미 더 코인",
    description: "휴리스틱 AI 시뮬레이션 · 코인 비공개 제출 + 베팅 (숨김정보)",
    icon: "🪙",
  },
  {
    gameType: "love-wins-all",
    scenario: "heuristic-2p",
    title: "러브 윈즈 올",
    description: "휴리스틱 AI 시뮬레이션 · 가위바위보 족보 + 베팅 (숨김정보)",
    icon: "💕",
  },
  {
    gameType: "cube-hell",
    scenario: "heuristic-2p",
    title: "큐브지옥",
    description: "휴리스틱 AI 시뮬레이션 · 3×3×3 숨김 큐브 추리 (숨김정보)",
    icon: "🧊",
  },
] as const;

function ReplayCatalogSection() {
  return (
    <div className="mt-6 w-full max-w-2xl" data-testid="replay-catalog-section">
      <div className="mb-3 font-semibold text-sm text-text-secondary">
        리플레이 / 시뮬레이션 (백엔드 불필요)
      </div>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          data-testid="enter-replay-room"
          onClick={() => navigateTo(buildReplayLobbyPath())}
          className="flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border-subtle bg-surface-secondary p-4 text-left transition-colors hover:border-accent-blue"
        >
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-blue-bg text-xl">
            ⏯
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-base">리플레이 룸</div>
            <div className="mt-0.5 text-text-muted text-xs">
              공유 코드 또는 저장한 파일로 지난 게임 다시 보기
            </div>
          </div>
          <div className="shrink-0 text-lg text-text-hint">›</div>
        </button>
        {REPLAY_ENTRIES.map((entry) => (
          <button
            type="button"
            key={`${entry.gameType}-${entry.scenario}`}
            data-testid={`enter-replay-${entry.gameType}`}
            onClick={() =>
              navigateTo(buildGameReplayPath(entry.gameType, entry.scenario))
            }
            className="flex w-full cursor-pointer items-start gap-3 rounded-lg border border-border-subtle bg-surface-secondary p-4 text-left transition-colors hover:border-accent-blue"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent-blue-bg text-xl">
              {entry.icon}
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-base">{entry.title}</div>
              <div className="mt-0.5 text-text-muted text-xs">
                {entry.description}
              </div>
            </div>
            <div className="shrink-0 text-lg text-text-hint">›</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function buildHomeGameEntryTestId(gameType: string): string {
  return `enter-lobby-${gameType}`;
}

export function HomeScreen() {
  const { user } = useAuth();
  const authActions = useAuthActions();
  const catalog = useGameCatalog();
  const docsIndex = useGameDocsIndex();
  const [sortMode, setSortMode] = useState<GameSortMode>("added-default");
  const [filters, setFilters] = useState<GameListFilters>(emptyGameListFilters);
  const [showDetails, setShowDetails] = useState(false);
  const visibleGames = getVisibleGameTypes(catalog.games, sortMode, filters);

  function updateFilter(filterName: keyof GameListFilters, checked: boolean) {
    setFilters((currentFilters) => ({
      ...currentFilters,
      [filterName]: checked,
    }));
  }

  return (
    <div className="flex min-h-screen flex-col items-center p-3 sm:p-6">
      {/* Header */}
      <div className="mb-4 flex w-full max-w-2xl flex-col items-start justify-between gap-2 sm:mb-6 sm:flex-row sm:items-center sm:gap-0">
        <div>
          <h1 className="m-0 font-bold text-xl">게임 선택</h1>
          <p className="mt-1 text-sm text-text-muted">
            플레이할 게임을 선택하세요
          </p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            data-testid="user-label"
            className="text-sm text-text-secondary"
          >
            {getUserLabel(user)}
          </span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => authActions.logout()}
          >
            로그아웃
          </Button>
        </div>
      </div>

      {/* Game Cards */}
      <div className="w-full max-w-2xl">
        <div className="mb-3 rounded-lg border border-border-subtle bg-surface-primary p-3 shadow-sm">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <label className="flex flex-col gap-1 text-sm text-text-secondary">
              <span className="font-medium text-text-primary">정렬</span>
              <select
                data-testid="game-sort-select"
                value={sortMode}
                onChange={(event) =>
                  setSortMode(event.currentTarget.value as GameSortMode)
                }
                className="min-h-11 rounded-md border border-border-primary bg-surface-tertiary px-3 text-sm text-text-primary"
              >
                {gameSortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>

            <fieldset className="min-w-0">
              <legend className="mb-1 font-medium text-sm text-text-primary">
                필터
              </legend>
              <div className="flex flex-wrap gap-2">
                <label className="flex min-h-11 items-center gap-2 rounded-md border border-border-primary bg-surface-tertiary px-3 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    data-testid="two-player-filter"
                    checked={filters.twoPlayer}
                    onChange={(event) =>
                      updateFilter("twoPlayer", event.currentTarget.checked)
                    }
                  />
                  <span>2인 가능</span>
                </label>
                <label className="flex min-h-11 items-center gap-2 rounded-md border border-border-primary bg-surface-tertiary px-3 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    data-testid="eight-plus-filter"
                    checked={filters.eightPlus}
                    onChange={(event) =>
                      updateFilter("eightPlus", event.currentTarget.checked)
                    }
                  />
                  <span>8인 이상</span>
                </label>
                <label className="flex min-h-11 items-center gap-2 rounded-md border border-border-primary bg-surface-tertiary px-3 text-sm text-text-secondary">
                  <input
                    type="checkbox"
                    data-testid="ai-support-filter"
                    checked={filters.aiSupported}
                    onChange={(event) =>
                      updateFilter("aiSupported", event.currentTarget.checked)
                    }
                  />
                  <span>AI 대전</span>
                </label>
              </div>
            </fieldset>

            <Button
              type="button"
              variant="secondary"
              size="sm"
              aria-pressed={showDetails}
              aria-expanded={showDetails}
              data-testid="toggle-game-details"
              onClick={() => setShowDetails((current) => !current)}
              className="shrink-0"
            >
              {showDetails ? "세부 정보 숨기기" : "세부 정보 보기"}
            </Button>
          </div>
          <p className="mt-2 text-text-muted text-xs" role="status">
            {visibleGames.length} / {catalog.games.length}개 표시
          </p>
        </div>

        {catalog.status === "error" && (
          <Alert className="mb-4">{catalog.error}</Alert>
        )}
        {catalog.status === "loading" ? (
          <p className="text-sm text-text-secondary">
            게임 목록 불러오는 중...
          </p>
        ) : (
          <GameCatalogList
            games={visibleGames}
            showDetails={showDetails}
            entryTestIdBuilder={buildHomeGameEntryTestId}
            emptyMessage="조건에 맞는 게임이 없습니다."
            docsIndex={docsIndex}
          />
        )}

        <ReplayCatalogSection />
      </div>
    </div>
  );
}
